import { useCallback, useEffect, useState } from 'react'

import { MergeTagContext } from 'shared/gen/manager/core/proto/merge-tags'
import { hydrateMergeTags } from 'shared/grpc/merge-tags'
import { processServerAction } from 'shared/services/server-action/server-action-client-utils'
import { toastError } from 'shared/timber/v1/Toast'

type FetchParams = {
  showLoading?: boolean
}

type UseMergeTags = {
  skip?: boolean
  unitId: string
  templateText: string
  mergeTagContext: MergeTagContext
}

type UseMergeTagsReturn = {
  data?: string | null
  loading: boolean
  refetch: (params: FetchParams) => void
}

export default function useMergeTags({
  skip = false,
  unitId,
  templateText,
  mergeTagContext,
}: UseMergeTags): UseMergeTagsReturn {
  const [data, setData] = useState<string | null>()
  const [loading, setLoading] = useState(false)

  const fetchData = useCallback(
    async ({ showLoading = false }: FetchParams) => {
      try {
        if (skip) return
        if (showLoading) setLoading(true)

        const { hydratedText } = await processServerAction(
          hydrateMergeTags({
            unitId,
            templateText,
            mergeTagContext,
          }),
          'Something went wrong when loading the unit merge tags.',
        )

        setData(hydratedText)
      } catch (error) {
        toastError(error)
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [skip, unitId, templateText, mergeTagContext],
  )

  useEffect(() => {
    fetchData({ showLoading: true })
  }, [fetchData])

  return { data, refetch: fetchData, loading }
}
